import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { 
  Warehouse, 
  FloppyDisk,
  Check,
  PlusCircle,
  MagnifyingGlass,
  X,
  Pencil,
  Trash,
  ArrowUp,
  ArrowDown,
  FloppyDiskBack,
  CloudArrowUp,
  Eye,
  CalendarBlank,
  ClockCounterClockwise
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProductCountRow } from "../components/inventory/ProductCountRow";
import { PrepItemCountRow } from "../components/inventory/PrepItemCountRow";
import { HistoryView } from "../components/inventory/HistoryView";
import { CalendarView } from "../components/inventory/CalendarView";
import { CountingOrderDialog } from "../components/inventory/CountingOrderDialog";
import { AddProductDialog } from "../components/inventory/AddProductDialog";
import { EditProductDialog } from "../components/inventory/EditProductDialog";
import { PublishDialog } from "../components/inventory/PublishDialog";
import { EditRecordDialog } from "../components/inventory/EditRecordDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

// Parse unit string to extract conversion info
// Examples: "1 CS = 12 EA", "1 CS = 20lb", "1 CS = 5 x 2.2lb", "1 EA = 50lb"
const parseUnitString = (unitStr) => {
  if (!unitStr) return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: 1 };
  
  const str = unitStr.toUpperCase();
  
  // Pattern: "1 CS = X EA" or "1 CS = X Bag"
  let match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(EA|BAG|BAGS|PC|PCS)/i);
  if (match) {
    return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: parseFloat(match[2]) };
  }
  
  // Pattern: "1 CS = X x Y lb" (e.g., "1 CS = 5 x 2.2lb" = 5 bags)
  match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*[Xx]\s*([\d.]+)\s*(LB|LBS)/i);
  if (match) {
    const bags = parseFloat(match[2]);
    const lbPerBag = parseFloat(match[3]);
    return { primaryUnit: 'CS', secondaryUnit: 'BAG', conversion: bags, lbPerBag };
  }
  
  // Pattern: "1 CS = Xlb" or "1 CS = X lb"
  match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(LB|LBS)/i);
  if (match) {
    return { primaryUnit: 'CS', secondaryUnit: 'LB', conversion: parseFloat(match[2]) };
  }
  
  // Pattern: "1 EA = Xlb"
  match = str.match(/1\s*EA\s*=\s*([\d.]+)\s*(LB|LBS)/i);
  if (match) {
    return { primaryUnit: 'EA', secondaryUnit: 'LB', conversion: parseFloat(match[1]) };
  }
  
  // Pattern: "1 CS = X OZ"
  match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(OZ)/i);
  if (match) {
    return { primaryUnit: 'CS', secondaryUnit: 'OZ', conversion: parseFloat(match[2]) };
  }
  
  // Pattern: "1 EA = X Gallon"
  match = str.match(/1\s*EA\s*=\s*([\d.]+)\s*(GAL|GALLON)/i);
  if (match) {
    return { primaryUnit: 'EA', secondaryUnit: 'GAL', conversion: parseFloat(match[1]) };
  }
  
  // Default: just show EA
  if (str.includes('EA') || str.includes('EACH')) {
    return { primaryUnit: 'EA', secondaryUnit: null, conversion: 1 };
  }
  
  // Default fallback
  return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: 1 };
};

// Get display label for unit
const getUnitLabel = (unit) => {
  const labels = {
    'CS': 'Case',
    'EA': 'Each',
    'BAG': 'Bag',
    'LB': 'Pounds',
    'OZ': 'Ounces',
    'GAL': 'Gallon'
  };
  return labels[unit] || unit;
};

export default function Inventory() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [locations, setLocations] = useState([]);
  const [storageAreas, setStorageAreas] = useState([]);
  const [allStorageAreas, setAllStorageAreas] = useState([]);  // All storage areas for history filter
  const [products, setProducts] = useState([]);
  const [prepItems, setPrepItems] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [recordType, setRecordType] = useState("weekly");
  // Inventory counts keyed by storage area: { areaId: { productId: { primary, secondary } } }
  const [inventoryCounts, setInventoryCounts] = useState({});
  // Prep counts keyed by storage area: { areaId: { prepId: quantity } }
  const [prepCounts, setPrepCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", unit: "1 CS = 1 EA", category: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  
  // All counts stored by location: { locationId: { inventoryCounts: {}, prepCounts: {} } }
  const [allLocationCounts, setAllLocationCounts] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('inventoryDraft');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Admin edit states
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", unit: "", price: "", category: "" });

  // Backdating (admin only)
  const [countDate, setCountDate] = useState(format(new Date(), "yyyy-MM-dd"));
  
  // History tab
  const [activeTab, setActiveTab] = useState("count");
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLocation, setHistoryLocation] = useState("all");
  const [historyStorageArea, setHistoryStorageArea] = useState("all");
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [editRecordForm, setEditRecordForm] = useState({ count_cs: 0, count_ea: 0, count_lb: 0, count_oz: 0, notes: "", recorded_at: "", location_id: "" });
  const [historySearch, setHistorySearch] = useState("");
  const [historyItemType, setHistoryItemType] = useState("all"); // "all", "product", "prep"

  // Counting status per storage area
  const [countingStatus, setCountingStatus] = useState([]);

  // Calendar tab
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [submissions, setSubmissions] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [newSubmissionDate, setNewSubmissionDate] = useState("");
  const [calendarLocation, setCalendarLocation] = useState("all");
  const [updatingSubmission, setUpdatingSubmission] = useState(false);

  // Counting order dialog
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragItem, setDragItem] = useState(null);

  const filteredHistoryRecords = useMemo(() => {
    if (!historySearch) return historyRecords;
    const q = historySearch.toLowerCase();
    return historyRecords.filter(r =>
      r.product_name?.toLowerCase().includes(q) ||
      r.location_name?.toLowerCase().includes(q) ||
      r.storage_area_name?.toLowerCase().includes(q) ||
      r.recorded_by?.toLowerCase().includes(q)
    );
  }, [historyRecords, historySearch]);

  // Get storage areas for the selected history location
  const historyStorageAreas = useMemo(() => {
    if (historyLocation === "all") return [];
    return allStorageAreas.filter(a => a.location_id === historyLocation);
  }, [historyLocation, allStorageAreas]);

  // Save all counts to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(allLocationCounts).length > 0) {
      localStorage.setItem('inventoryDraft', JSON.stringify(allLocationCounts));
    }
  }, [allLocationCounts]);

  // Auto-save current location counts to allLocationCounts when counts change
  useEffect(() => {
    if (selectedLocation && (Object.keys(inventoryCounts).length > 0 || Object.keys(prepCounts).length > 0)) {
      setAllLocationCounts(prev => ({
        ...prev,
        [selectedLocation]: {
          inventoryCounts,
          prepCounts,
          recordType
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryCounts, prepCounts, selectedLocation, recordType]);

  // Load counts for selected location from allLocationCounts
  useEffect(() => {
    if (selectedLocation && allLocationCounts[selectedLocation]) {
      const saved = allLocationCounts[selectedLocation];
      setInventoryCounts(saved.inventoryCounts || {});
      setPrepCounts(saved.prepCounts || {});
      if (saved.recordType) setRecordType(saved.recordType);
    } else if (selectedLocation) {
      setInventoryCounts({});
      setPrepCounts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  // Reset counters when navigating from Dashboard with startNewCount state
  useEffect(() => {
    if (location.state?.startNewCount) {
      setInventoryCounts({});
      setPrepCounts({});
      setAllLocationCounts({});
      localStorage.removeItem('inventoryDraft');
      // Clear the state to prevent re-reset on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchLocations();
    fetchAllStorageAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchStorageAreas(selectedLocation);
      fetchProducts(selectedLocation);
      fetchPrepItems(selectedLocation);
      fetchCountingStatus(selectedLocation, countDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, countDate]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0].location_id);
      }
    } catch (error) {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageAreas = async (locationId) => {
    try {
      const response = await axios.get(`${API}/storage-areas?location_id=${locationId}`, { withCredentials: true });
      setStorageAreas(response.data);
      if (response.data.length > 0) {
        setSelectedArea(response.data[0].area_id);
      } else {
        setSelectedArea(null);
      }
    } catch (error) {
      toast.error("Failed to load storage areas");
    }
  };

  const fetchAllStorageAreas = async () => {
    try {
      const response = await axios.get(`${API}/storage-areas`, { withCredentials: true });
      setAllStorageAreas(response.data);
    } catch (error) {
      console.error("Failed to load all storage areas");
    }
  };

  const fetchCountingStatus = async (locationId, date) => {
    try {
      const response = await axios.get(`${API}/inventory/counting-status?location_id=${locationId}&count_date=${date}`, { withCredentials: true });
      setCountingStatus(response.data);
    } catch (error) {
      console.error("Failed to load counting status");
    }
  };

  const fetchSubmissions = useCallback(async (month) => {
    setCalendarLoading(true);
    try {
      const start = format(startOfMonth(month), "yyyy-MM-dd");
      const end = format(endOfMonth(month), "yyyy-MM-dd");
      let url = `${API}/inventory/submissions?start_date=${start}&end_date=${end}`;
      if (calendarLocation !== "all") url += `&location_id=${calendarLocation}`;
      const res = await axios.get(url, { withCredentials: true });
      setSubmissions(res.data);
    } catch {
      console.error("Failed to load submissions");
    } finally {
      setCalendarLoading(false);
    }
  }, [calendarLocation]);

  const fetchProducts = async (locationId) => {
    try {
      const response = await axios.get(`${API}/products?location_id=${locationId}`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    }
  };

  const fetchPrepItems = async (locationId) => {
    try {
      const response = await axios.get(`${API}/prep-items/inventory/${locationId}`, { withCredentials: true });
      setPrepItems(response.data);
    } catch (error) {
      // Silently fail - prep items are optional
      setPrepItems([]);
    }
  };

  // Handle prep item count change
  const handlePrepCountChange = (prepId, value) => {
    setPrepCounts(prev => ({
      ...prev,
      [selectedArea]: {
        ...(prev[selectedArea] || {}),
        [prepId]: Math.max(0, parseFloat(value) || 0)
      }
    }));
  };

  const incrementPrepCount = (prepId) => {
    setPrepCounts(prev => ({
      ...prev,
      [selectedArea]: {
        ...(prev[selectedArea] || {}),
        [prepId]: ((prev[selectedArea]?.[prepId]) || 0) + 1
      }
    }));
  };

  const decrementPrepCount = (prepId) => {
    setPrepCounts(prev => ({
      ...prev,
      [selectedArea]: {
        ...(prev[selectedArea] || {}),
        [prepId]: Math.max(0, ((prev[selectedArea]?.[prepId]) || 0) - 1)
      }
    }));
  };

  // Handle unit input change — scoped to current storage area
  const handleUnitChange = (productId, unitType, value) => {
    setInventoryCounts(prev => ({
      ...prev,
      [selectedArea]: {
        ...(prev[selectedArea] || {}),
        [productId]: {
          ...(prev[selectedArea]?.[productId] || {}),
          [unitType]: Math.max(0, parseFloat(value) || 0)
        }
      }
    }));
  };

  const incrementUnit = (productId, unitType) => {
    setInventoryCounts(prev => ({
      ...prev,
      [selectedArea]: {
        ...(prev[selectedArea] || {}),
        [productId]: {
          ...(prev[selectedArea]?.[productId] || {}),
          [unitType]: ((prev[selectedArea]?.[productId]?.[unitType]) || 0) + 1
        }
      }
    }));
  };

  const decrementUnit = (productId, unitType) => {
    setInventoryCounts(prev => ({
      ...prev,
      [selectedArea]: {
        ...(prev[selectedArea] || {}),
        [productId]: {
          ...(prev[selectedArea]?.[productId] || {}),
          [unitType]: Math.max(0, ((prev[selectedArea]?.[productId]?.[unitType]) || 0) - 1)
        }
      }
    }));
  };

  // Calculate total in primary unit (CS or EA) and value — reads from current area
  const calculateTotal = (productId) => {
    const counts = inventoryCounts[selectedArea]?.[productId] || {};
    const product = products.find(p => p.product_id === productId);
    const unitInfo = parseUnitString(product?.unit);
    
    let total = counts.primary || 0;
    
    if (counts.secondary && unitInfo.conversion > 0) {
      total += counts.secondary / unitInfo.conversion;
    }
    
    const price = product?.price || 0;
    const value = total * price;
    
    return { 
      total: total.toFixed(2), 
      value: value.toFixed(2),
      hasValue: total > 0
    };
  };

  const saveInventory = async () => {
    // Build records from ALL storage areas (each area has its own counts)
    const records = [];
    Object.entries(inventoryCounts).forEach(([areaId, areaCounts]) => {
      Object.entries(areaCounts).forEach(([productId, counts]) => {
        if (!counts || ((counts.primary || 0) === 0 && (counts.secondary || 0) === 0)) return;
        const product = products.find(p => p.product_id === productId);
        const unitInfo = parseUnitString(product?.unit);
        
        let totalPrimary = counts.primary || 0;
        if (counts.secondary && unitInfo.conversion > 0) {
          totalPrimary += counts.secondary / unitInfo.conversion;
        }
        
        records.push({
          product_id: productId,
          location_id: selectedLocation,
          storage_area_id: areaId,
          quantity: totalPrimary,
          count_cs: unitInfo.primaryUnit === 'CS' ? (counts.primary || 0) : 0,
          count_ea: unitInfo.primaryUnit === 'EA' ? (counts.primary || 0) : (unitInfo.secondaryUnit === 'EA' ? (counts.secondary || 0) : 0),
          count_lb: unitInfo.secondaryUnit === 'LB' ? (counts.secondary || 0) : 0,
          count_oz: unitInfo.secondaryUnit === 'OZ' ? (counts.secondary || 0) : 0,
          record_type: recordType,
          recorded_at: countDate !== format(new Date(), "yyyy-MM-dd") ? countDate : null
        });
      });
    });

    // Prep item records from ALL storage areas
    const prepRecords = [];
    Object.entries(prepCounts).forEach(([areaId, areaPrepCounts]) => {
      Object.entries(areaPrepCounts).forEach(([prepId, quantity]) => {
        if (!quantity || quantity <= 0) return;
        prepRecords.push({
          prep_id: prepId,
          location_id: selectedLocation,
          storage_area_id: areaId,
          quantity: quantity,
          record_type: recordType,
          recorded_at: countDate !== format(new Date(), "yyyy-MM-dd") ? countDate : null
        });
      });
    });

    if (records.length === 0 && prepRecords.length === 0) {
      toast.error("No inventory counts to save");
      return;
    }

    setSaving(true);
    try {
      const promises = [];
      if (records.length > 0) {
        promises.push(axios.post(`${API}/inventory/batch`, records, { withCredentials: true }));
      }
      if (prepRecords.length > 0) {
        promises.push(axios.post(`${API}/inventory/prep/batch`, prepRecords, { withCredentials: true }));
      }
      await Promise.all(promises);
      
      const totalSaved = records.length + prepRecords.length;
      toast.success(`Saved ${totalSaved} inventory records`);
      setInventoryCounts({});
      setPrepCounts({});
    } catch (error) {
      toast.error("Failed to save inventory");
    } finally {
      setSaving(false);
    }
  };

  // Publish all inventory counts across all locations
  const publishAllInventory = async () => {
    const allRecords = [];
    const allPrepRecords = [];
    
    for (const [locId, locData] of Object.entries(allLocationCounts)) {
      const { inventoryCounts: locInvCounts, prepCounts: locPrepCounts, recordType: locRecordType } = locData;
      
      try {
        const prodRes = await axios.get(`${API}/products?location_id=${locId}`, { withCredentials: true });
        const locProducts = prodRes.data;
        
        // Process inventory counts — now keyed by areaId -> productId
        Object.entries(locInvCounts || {}).forEach(([areaId, areaCounts]) => {
          Object.entries(areaCounts || {}).forEach(([productId, counts]) => {
            if (counts && ((counts.primary || 0) > 0 || (counts.secondary || 0) > 0)) {
              const product = locProducts.find(p => p.product_id === productId);
              const unitInfo = parseUnitString(product?.unit);
              
              let totalPrimary = counts.primary || 0;
              if (counts.secondary && unitInfo.conversion > 0) {
                totalPrimary += counts.secondary / unitInfo.conversion;
              }
              
              allRecords.push({
                product_id: productId,
                location_id: locId,
                storage_area_id: areaId,
                quantity: totalPrimary,
                count_cs: unitInfo.primaryUnit === 'CS' ? (counts.primary || 0) : 0,
                count_ea: unitInfo.primaryUnit === 'EA' ? (counts.primary || 0) : (unitInfo.secondaryUnit === 'EA' ? (counts.secondary || 0) : 0),
                count_lb: unitInfo.secondaryUnit === 'LB' ? (counts.secondary || 0) : 0,
                count_oz: unitInfo.secondaryUnit === 'OZ' ? (counts.secondary || 0) : 0,
                record_type: locRecordType || 'weekly',
                recorded_at: countDate !== format(new Date(), "yyyy-MM-dd") ? countDate : null
              });
            }
          });
        });
        
        // Process prep counts — now keyed by areaId -> prepId
        Object.entries(locPrepCounts || {}).forEach(([areaId, areaPrepCounts]) => {
          Object.entries(areaPrepCounts || {}).forEach(([prepId, quantity]) => {
            if (quantity > 0) {
              allPrepRecords.push({
                prep_id: prepId,
                location_id: locId,
                storage_area_id: areaId,
                quantity: quantity,
                record_type: locRecordType || 'weekly',
                recorded_at: countDate !== format(new Date(), "yyyy-MM-dd") ? countDate : null
              });
            }
          });
        });
      } catch (error) {
        console.error(`Failed to fetch products for location ${locId}`, error);
      }
    }

    if (allRecords.length === 0 && allPrepRecords.length === 0) {
      toast.error("No inventory counts to publish");
      return;
    }

    setSaving(true);
    try {
      const promises = [];
      if (allRecords.length > 0) {
        promises.push(axios.post(`${API}/inventory/batch`, allRecords, { withCredentials: true }));
      }
      if (allPrepRecords.length > 0) {
        promises.push(axios.post(`${API}/inventory/prep/batch`, allPrepRecords, { withCredentials: true }));
      }
      await Promise.all(promises);
      
      const totalSaved = allRecords.length + allPrepRecords.length;
      toast.success(`Published ${totalSaved} inventory records across all locations`);
      
      // Clear all counts
      setInventoryCounts({});
      setPrepCounts({});
      setAllLocationCounts({});
      localStorage.removeItem('inventoryDraft');
      setPublishDialogOpen(false);
    } catch (error) {
      toast.error("Failed to publish inventory");
    } finally {
      setSaving(false);
    }
  };

  // ====== HISTORY TAB FUNCTIONS ======
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      let url = `${API}/inventory/history?`;
      if (historyLocation !== "all") url += `location_id=${historyLocation}&`;
      if (historyStorageArea !== "all") url += `storage_area_id=${historyStorageArea}&`;
      if (historyStartDate) url += `start_date=${historyStartDate}&`;
      if (historyEndDate) url += `end_date=${historyEndDate}&`;
      if (historyItemType !== "all") url += `item_type=${historyItemType}&`;
      const res = await axios.get(url, { withCredentials: true });
      setHistoryRecords(res.data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history" && locations.length > 0) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyLocation, historyStorageArea, historyStartDate, historyEndDate, historyItemType]);

  useEffect(() => {
    if (activeTab === "calendar" && locations.length > 0) {
      fetchSubmissions(calendarMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, calendarMonth, calendarLocation, fetchSubmissions]);

  // Reset storage area when location changes
  useEffect(() => {
    setHistoryStorageArea("all");
  }, [historyLocation]);

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setEditRecordForm({
      count_cs: record.count_cs || 0,
      count_ea: record.item_type === "prep" ? record.quantity : (record.count_ea || 0),
      count_lb: record.count_lb || 0,
      count_oz: record.count_oz || 0,
      notes: record.notes || "",
      recorded_at: record.recorded_at?.slice(0, 10) || "",
      location_id: record.location_id || ""
    });
  };

  const handleSaveRecord = async () => {
    if (!editingRecord) return;
    try {
      if (editingRecord.item_type === "prep") {
        // Use prep inventory update endpoint
        await axios.put(`${API}/inventory/prep/${editingRecord.record_id}`, {
          quantity: editRecordForm.count_ea,
          recorded_at: editRecordForm.recorded_at,
          location_id: editRecordForm.location_id,
          notes: editRecordForm.notes
        }, { withCredentials: true });
      } else {
        // Use product inventory update endpoint
        await axios.put(`${API}/inventory/${editingRecord.record_id}`, {
          count_cs: editRecordForm.count_cs,
          count_ea: editRecordForm.count_ea,
          count_lb: editRecordForm.count_lb,
          count_oz: editRecordForm.count_oz,
          notes: editRecordForm.notes,
          recorded_at: editRecordForm.recorded_at,
          location_id: editRecordForm.location_id
        }, { withCredentials: true });
      }
      toast.success("Record updated");
      setEditingRecord(null);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update record");
    }
  };

  const handleDeleteRecord = async (record) => {
    const recordId = typeof record === 'string' ? record : record.record_id;
    const itemType = typeof record === 'string' ? null : record.item_type;
    if (!window.confirm("Delete this inventory record? This cannot be undone.")) return;
    try {
      if (itemType === "prep") {
        await axios.delete(`${API}/inventory/prep/${recordId}`, { withCredentials: true });
      } else {
        await axios.delete(`${API}/inventory/${recordId}`, { withCredentials: true });
      }
      toast.success("Record deleted");
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete record");
    }
  };

  // Calendar submission date update
  const handleUpdateSubmissionDate = async () => {
    if (!selectedSubmission || !newSubmissionDate) return;
    setUpdatingSubmission(true);
    try {
      await axios.put(`${API}/inventory/submissions/update-date`, {
        submission_key: selectedSubmission.submission_key,
        new_date: newSubmissionDate
      }, { withCredentials: true });
      toast.success(`Moved ${selectedSubmission.total_items} records from ${selectedSubmission.date} to ${newSubmissionDate}`);
      setSelectedSubmission(null);
      setNewSubmissionDate("");
      fetchSubmissions(calendarMonth);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update submission date");
    } finally {
      setUpdatingSubmission(false);
    }
  };

  // Counting order functions
  const openOrderDialog = () => {
    if (!selectedLocation || !selectedArea) {
      toast.error("Select a location and storage area first");
      return;
    }
    // Build combined list: products + prep items for this area
    const items = [];
    const areaProducts = products.filter(p => p.storage_area_id === selectedArea);
    areaProducts.forEach((p, idx) => {
      const mapping = p.location_mappings?.find(m => m.location_id === selectedLocation);
      items.push({
        item_id: p.product_id,
        item_type: "product",
        name: p.name,
        order: mapping?.order ?? idx,
        unit: p.unit
      });
    });
    const areaPrepItems = prepItems.filter(p => p.storage_area_ids?.includes(selectedArea));
    areaPrepItems.forEach((p, idx) => {
      items.push({
        item_id: p.prep_id,
        item_type: "prep",
        name: p.name,
        order: (p.order ?? areaProducts.length + idx),
        unit: p.yield_unit || "EA"
      });
    });
    items.sort((a, b) => a.order - b.order);
    setOrderItems(items);
    setOrderSearch("");
    setOrderDialogOpen(true);
  };

  const handleOrderChange = (itemId, newOrder) => {
    setOrderItems(prev => prev.map(item => 
      item.item_id === itemId ? { ...item, order: parseInt(newOrder) || 0 } : item
    ));
  };

  const handleDragStart = (idx) => setDragItem(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (dropIdx) => {
    if (dragItem === null || dragItem === dropIdx) return;
    setOrderItems(prev => {
      const items = [...prev];
      const [removed] = items.splice(dragItem, 1);
      items.splice(dropIdx, 0, removed);
      return items.map((item, idx) => ({ ...item, order: idx }));
    });
    setDragItem(null);
  };

  const saveCountingOrder = async () => {
    setSavingOrder(true);
    try {
      await axios.put(`${API}/inventory/counting-order`, {
        location_id: selectedLocation,
        storage_area_id: selectedArea,
        items: orderItems.map((item, idx) => ({
          item_id: item.item_id,
          item_type: item.item_type,
          order: idx
        }))
      }, { withCredentials: true });
      toast.success("Counting order saved");
      setOrderDialogOpen(false);
      fetchProducts(selectedLocation); // Refresh products with new order
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save order");
    } finally {
      setSavingOrder(false);
    }
  };

  // Group submissions by date for calendar rendering — now handled in CalendarView component



  // Get summary of all pending counts
  const getPendingSummary = () => {
    const summary = [];
    for (const [locId, locData] of Object.entries(allLocationCounts)) {
      const loc = locations.find(l => l.location_id === locId);
      // Count unique products across all areas
      let invCount = 0;
      let prepCount = 0;
      Object.values(locData.inventoryCounts || {}).forEach(areaCounts => {
        invCount += Object.values(areaCounts || {}).filter(c => c && ((c.primary || 0) > 0 || (c.secondary || 0) > 0)).length;
      });
      Object.values(locData.prepCounts || {}).forEach(areaPrepCounts => {
        prepCount += Object.values(areaPrepCounts || {}).filter(q => q > 0).length;
      });
      if (invCount > 0 || prepCount > 0) {
        summary.push({
          locationId: locId,
          locationName: loc?.name || locId,
          productCount: invCount,
          prepCount: prepCount
        });
      }
    }
    return summary;
  };

  const getTotalPendingCounts = () => {
    return getPendingSummary().reduce((acc, s) => acc + s.productCount + s.prepCount, 0);
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !selectedArea) {
      toast.error("Please fill in product name and select a storage area");
      return;
    }

    try {
      await axios.post(`${API}/products`, {
        ...newProduct,
        storage_area_id: selectedArea
      }, { withCredentials: true });
      toast.success("Product added");
      setAddProductOpen(false);
      setNewProduct({ name: "", unit: "1 CS = 1 EA", category: "" });
      fetchProducts(selectedLocation);
    } catch (error) {
      toast.error("Failed to add product");
    }
  };

  // Admin functions
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditForm({
      name: product.name,
      unit: product.unit,
      price: product.price?.toString() || "0",
      category: product.category || ""
    });
  };

  const handleSaveEdit = async () => {
    if (!editingProduct) return;
    try {
      await axios.put(`${API}/products/${editingProduct.product_id}`, {
        name: editForm.name,
        unit: editForm.unit,
        price: parseFloat(editForm.price) || 0,
        category: editForm.category
      }, { withCredentials: true });
      toast.success("Product updated");
      setEditingProduct(null);
      fetchProducts(selectedLocation);
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted");
      fetchProducts(selectedLocation);
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleMoveProduct = async (product, direction) => {
    const currentProducts = filteredProducts;
    const currentIndex = currentProducts.findIndex(p => p.product_id === product.product_id);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= currentProducts.length) return;
    
    const swapProduct = currentProducts[newIndex];
    
    try {
      await Promise.all([
        axios.put(`${API}/products/${product.product_id}`, { order: swapProduct.order }, { withCredentials: true }),
        axios.put(`${API}/products/${swapProduct.product_id}`, { order: product.order }, { withCredentials: true })
      ]);
      fetchProducts(selectedLocation);
    } catch (error) {
      toast.error("Failed to reorder");
    }
  };

  // Filter products - check location_mappings array for multi-area support
  const filteredProducts = products
    .filter(p => {
      // If searching, show all products matching search
      if (searchQuery) {
        return p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.category?.toLowerCase().includes(searchQuery.toLowerCase());
      }
      
      // If a storage area is selected, check if product is mapped to that area
      if (selectedArea) {
        // Check storage_area_ids array from backend (preferred)
        if (p.storage_area_ids && p.storage_area_ids.length > 0) {
          return p.storage_area_ids.includes(selectedArea);
        }
        // Fallback: check location_mappings array
        const mappings = p.location_mappings || [];
        const inSelectedArea = mappings.some(m => m.storage_area_id === selectedArea);
        // Also check legacy storage_area_id field for backward compatibility
        return inSelectedArea || p.storage_area_id === selectedArea;
      }
      
      return true;
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Filter prep items by search AND storage area
  const filteredPrepItems = prepItems.filter(p => {
    // Search filter
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    // Storage area filter - if prep has storage_area_ids, filter by selected area
    if (selectedArea && p.storage_area_ids && p.storage_area_ids.length > 0) {
      return p.storage_area_ids.includes(selectedArea);
    }
    // If no storage_area_ids assigned, show in all areas (backward compat)
    return true;
  });

  // Prep items that have NO storage area assignment (show in separate section)
  const unassignedPrepItems = prepItems.filter(p => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return !p.storage_area_ids || p.storage_area_ids.length === 0;
  });

  const getCountsForProduct = () => {
    let productCounts = 0;
    Object.values(inventoryCounts).forEach(areaCounts => {
      productCounts += Object.values(areaCounts || {}).filter(c => c && ((c.primary || 0) > 0 || (c.secondary || 0) > 0)).length;
    });
    
    let prepCountsNum = 0;
    Object.values(prepCounts).forEach(areaPrepCounts => {
      prepCountsNum += Object.values(areaPrepCounts || {}).filter(q => q > 0).length;
    });
    
    return productCounts + prepCountsNum;
  };

  // Calculate total inventory value
  const getTotalInventoryValue = () => {
    let totalValue = 0;
    
    // Product values
    Object.entries(inventoryCounts).forEach(([productId, counts]) => {
      if (counts && ((counts.primary || 0) > 0 || (counts.secondary || 0) > 0)) {
        const product = products.find(p => p.product_id === productId);
        const unitInfo = parseUnitString(product?.unit);
        
        let total = counts.primary || 0;
        if (counts.secondary && unitInfo.conversion > 0) {
          total += counts.secondary / unitInfo.conversion;
        }
        
        totalValue += total * (product?.price || 0);
      }
    });
    
    // Prep item values
    Object.entries(prepCounts).forEach(([prepId, qty]) => {
      if (qty > 0) {
        const prep = prepItems.find(p => p.prep_id === prepId);
        totalValue += qty * (prep?.cost_per_unit || 0);
      }
    });
    
    return totalValue.toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Warehouse size={64} weight="duotone" className="text-muted-foreground/50 mb-4" />
        <h2 className="font-heading text-xl tracking-wide mb-2">No Locations Configured</h2>
        <p className="text-muted-foreground mb-4">
          {isAdmin 
            ? "Add locations in Settings to start recording inventory."
            : "Contact your admin to set up locations."}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="inventory-page" className="space-y-6 pb-32">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
            RECORD INVENTORY
          </h1>
          <p className="text-muted-foreground mt-1">
            Count items in their defined units
          </p>
        </div>
      </div>

      {/* Top-level tabs: Count | History */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="count" className="font-heading tracking-wide" data-testid="count-tab">
            <Warehouse size={16} className="mr-2" />
            COUNT
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="history" className="font-heading tracking-wide" data-testid="history-tab">
              <ClockCounterClockwise size={16} className="mr-2" />
              HISTORY
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="calendar" className="font-heading tracking-wide" data-testid="calendar-tab">
              <CalendarBlank size={16} className="mr-2" />
              CALENDAR
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="count" className="mt-4 space-y-6">

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Label className="text-sm text-muted-foreground mb-2 block">Location</Label>
          <Select value={selectedLocation || ""} onValueChange={(val) => {
            setSelectedLocation(val);
            setInventoryCounts({}); // Reset counters when changing location
          }}>
            <SelectTrigger data-testid="location-select" className="w-full">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <Label className="text-sm text-muted-foreground mb-2 block">Record Type</Label>
          <Select value={recordType} onValueChange={setRecordType}>
            <SelectTrigger data-testid="record-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <div className="w-full sm:w-48">
            <Label className="text-sm text-muted-foreground mb-2 block flex items-center gap-1">
              <CalendarBlank size={14} /> Count Date
            </Label>
            <Input
              type="date"
              value={countDate}
              onChange={(e) => setCountDate(e.target.value)}
              max={format(new Date(), "yyyy-MM-dd")}
              data-testid="count-date-input"
            />
            {countDate !== format(new Date(), "yyyy-MM-dd") && (
              <p className="text-xs text-amber-600 mt-1 font-medium">Backdating to {countDate}</p>
            )}
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input 
          data-testid="inventory-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search products by name or category..."
          className="pl-10 pr-10"
        />
        {searchQuery && (
          <button 
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Storage Area Tabs */}
      {storageAreas.length > 0 ? (
        <Tabs value={selectedArea || ""} onValueChange={setSelectedArea} className="w-full">
          <div className="overflow-x-auto">
            <TabsList className="inline-flex h-auto p-1 bg-muted/50 rounded-lg">
              {storageAreas.map(area => {
                const areaStatus = countingStatus.find(s => s.area_id === area.area_id);
                return (
                  <TabsTrigger 
                    key={area.area_id} 
                    value={area.area_id}
                    data-testid={`area-tab-${area.area_id}`}
                    className="font-heading text-xs sm:text-sm tracking-wide px-4 py-2 data-[state=active]:bg-white relative"
                  >
                    {area.name?.toUpperCase()}
                    {areaStatus && areaStatus.is_counted && (
                      <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-green-500 text-white rounded-full" title={`Counted by: ${areaStatus.counted_by.join(', ')}`}>
                        {areaStatus.total_count}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {storageAreas.map(area => {
            const areaPrepItems = filteredPrepItems.filter(p => p.storage_area_ids && p.storage_area_ids.includes(area.area_id));
            return (
            <TabsContent key={area.area_id} value={area.area_id} className="mt-4">
              <Card className="border rounded-xl">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div>
                    <CardTitle className="font-heading text-lg tracking-wide">
                      {area.name?.toUpperCase()}
                    </CardTitle>
                    {searchQuery && (
                      <p className="text-sm text-muted-foreground">
                        Showing {filteredProducts.length} results for "{searchQuery}"
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {isAdmin && (
                      <Button variant="outline" size="sm" onClick={openOrderDialog} data-testid="edit-order-btn">
                        <ArrowUp size={14} className="mr-1" /><ArrowDown size={14} className="mr-1" />
                        Order
                      </Button>
                    )}
                    <AddProductDialog
                      open={addProductOpen}
                      onOpenChange={setAddProductOpen}
                      newProduct={newProduct}
                      setNewProduct={setNewProduct}
                      onSave={handleAddProduct}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredProducts.length > 0 || areaPrepItems.length > 0 ? (
                    <div className="space-y-3">
                      {filteredProducts.map((product, idx) => {
                        const counts = inventoryCounts[area.area_id]?.[product.product_id] || {};
                        const { total, value, hasValue } = calculateTotal(product.product_id);
                        const unitInfo = parseUnitString(product.unit);
                        
                        return (
                          <ProductCountRow
                            key={product.product_id}
                            product={product} idx={idx} counts={counts}
                            total={total} value={value} hasValue={hasValue} unitInfo={unitInfo}
                            isAdmin={isAdmin} searchQuery={searchQuery}
                            onIncrement={incrementUnit} onDecrement={decrementUnit} onChange={handleUnitChange}
                            onMoveProduct={handleMoveProduct} onEditProduct={handleEditProduct} onDeleteProduct={handleDeleteProduct}
                            parseUnitString={parseUnitString} getUnitLabel={getUnitLabel}
                          />
                        );
                      })}
                      
                      {/* Inline Prep Items for this storage area */}
                      {areaPrepItems.map((prep, idx) => (
                        <PrepItemCountRow
                          key={prep.prep_id}
                          prep={prep} idx={idx}
                          quantity={prepCounts[area.area_id]?.[prep.prep_id]}
                          onIncrement={incrementPrepCount}
                          onDecrement={decrementPrepCount}
                          onChange={handlePrepCountChange}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No products in this area. Add items using the button above.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
          })}
        </Tabs>
      ) : (
        <Card className="border rounded-xl">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No storage areas configured for this location.
              {isAdmin && " Add storage areas in Settings."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Prep Items Section - only show unassigned prep items (those without storage area mappings) */}
      {unassignedPrepItems.length > 0 && (
        <Card className="border rounded-xl mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg tracking-wide">
              PREP ITEMS (UNASSIGNED)
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Count prepared items not assigned to a specific storage area
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {unassignedPrepItems.map((prep, idx) => (
                <PrepItemCountRow
                  key={prep.prep_id}
                  prep={prep} idx={idx}
                  quantity={prepCounts[selectedArea]?.[prep.prep_id]}
                  onIncrement={incrementPrepCount}
                  onDecrement={decrementPrepCount}
                  onChange={handlePrepCountChange}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Product Dialog (Admin only) */}
      <EditProductDialog
        editingProduct={editingProduct}
        setEditingProduct={setEditingProduct}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleSaveEdit}
      />

      {/* Publish Inventory Dialog */}
      <PublishDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        pendingSummary={getPendingSummary()}
        totalPendingCounts={getTotalPendingCounts()}
        saving={saving}
        onPublish={publishAllInventory}
      />

      {/* Bottom Action Bar - Sticky on Mobile */}
      {(getCountsForProduct() > 0 || getTotalPendingCounts() > 0) && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg lg:relative lg:p-0 lg:bg-transparent lg:border-0 lg:shadow-none">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-muted-foreground">This Location</p>
                <p className="text-xl font-semibold tabular-nums">{getCountsForProduct()} items</p>
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-xl font-semibold tabular-nums text-green-600">${getTotalInventoryValue()}</p>
              </div>
              {getTotalPendingCounts() > getCountsForProduct() && (
                <>
                  <div className="h-8 w-px bg-border" />
                  <div>
                    <p className="text-sm text-muted-foreground">All Locations</p>
                    <p className="text-xl font-semibold tabular-nums text-primary">{getTotalPendingCounts()} items</p>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setPublishDialogOpen(true)}
                disabled={getTotalPendingCounts() === 0}
                data-testid="review-publish-btn"
              >
                <Eye size={18} className="mr-2" />
                Review & Publish
              </Button>
            </div>
          </div>
        </div>
      )}
        </TabsContent>

        {/* History Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="history">
            <HistoryView
              historyItemType={historyItemType} setHistoryItemType={setHistoryItemType}
              historyLocation={historyLocation} setHistoryLocation={setHistoryLocation}
              historyStorageArea={historyStorageArea} setHistoryStorageArea={setHistoryStorageArea}
              historyStorageAreas={historyStorageAreas}
              historyStartDate={historyStartDate} setHistoryStartDate={setHistoryStartDate}
              historyEndDate={historyEndDate} setHistoryEndDate={setHistoryEndDate}
              historySearch={historySearch} setHistorySearch={setHistorySearch}
              historyLoading={historyLoading} historyRecords={historyRecords}
              filteredHistoryRecords={filteredHistoryRecords}
              locations={locations} fetchHistory={fetchHistory}
              onEditRecord={handleEditRecord} onDeleteRecord={handleDeleteRecord}
            />
          </TabsContent>
        )}

        {/* Calendar Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="calendar">
            <CalendarView
              calendarMonth={calendarMonth} setCalendarMonth={setCalendarMonth}
              calendarLocation={calendarLocation} setCalendarLocation={setCalendarLocation}
              calendarLoading={calendarLoading} submissions={submissions}
              locations={locations}
              selectedSubmission={selectedSubmission} setSelectedSubmission={setSelectedSubmission}
              newSubmissionDate={newSubmissionDate} setNewSubmissionDate={setNewSubmissionDate}
              updatingSubmission={updatingSubmission} onUpdateSubmissionDate={handleUpdateSubmissionDate}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Counting Order Dialog */}
      <CountingOrderDialog
        open={orderDialogOpen} onOpenChange={setOrderDialogOpen}
        orderItems={orderItems} orderSearch={orderSearch} setOrderSearch={setOrderSearch}
        onOrderChange={handleOrderChange} onDragStart={handleDragStart}
        onDragOver={handleDragOver} onDrop={handleDrop} dragItem={dragItem}
        savingOrder={savingOrder} onSave={saveCountingOrder}
      />

      {/* Edit Record Dialog */}
      <EditRecordDialog
        editingRecord={editingRecord}
        setEditingRecord={setEditingRecord}
        editRecordForm={editRecordForm}
        setEditRecordForm={setEditRecordForm}
        locations={locations}
        onSave={handleSaveRecord}
      />
    </div>
  );
}
